import { graphql } from 'gatsby';
import { IconicChevronLeft, IconicChevronRight } from '@insify/design-system/iconic-icons';
import { Article, ArticleCard } from './ArticleCard';
import { Section } from '~components/Section';
import { Heading } from '~components/Heading';
import { Carousel, CarouselPageIndicator, CarouselPagination, CarouselSlider } from '~components/Carousel';
import { applyMarker } from '~utils/applyMarker';

import * as styles from './ContentfulComponentRelatedArticles.module.css';

import type { ContentBlock } from '~components/types';
import type { Theme } from '~models/contentful';

export type ContentfulComponentRelatedArticlesContent = {
  __typename: 'ContentfulComponentRelatedArticles';
  relatedArticlesTitle: string;
  blogArticles: Article[];
  theme?: Theme;
};

type ContentfulComponentRelatedArticlesProps = {
  content: ContentfulComponentRelatedArticlesContent;
};

const ContentfulComponentRelatedArticles: ContentBlock<ContentfulComponentRelatedArticlesProps> = ({
  content: { relatedArticlesTitle, blogArticles },
  ...sectionProps
}) => {
  const itemCount = blogArticles.length;

  return (
    <Section {...sectionProps} className={styles.section}>
      <Heading level={1} className={styles.heading}>
        {applyMarker(relatedArticlesTitle, { animated: true, markerColor: 'var(--green-50)' })}
      </Heading>
      <Carousel
        config={{
          all: {
            slidesToShow: itemCount < 4 ? 1 : 3,
            slideGap: '0px',
            transitionDurationAutoplay: 1500,
            transitionDurationManual: 300,
            autoplayInterval: 5000,
            pauseAutoplayOnHover: true,
            infinite: true,
          },
          '(min-width: 672px)': {
            slidesToShow: itemCount < 3 ? itemCount : 2,
          },
          '(min-width: 1022px)': {
            slidesToShow: itemCount < 4 ? itemCount : 3,
          },
        }}
      >
        <CarouselSlider
          className={styles.carouselSlider}
          style={
            {
              '--slidesToShowMobile': itemCount < 4 ? 1 : 3,
              '--slidesToShow672': itemCount < 3 ? itemCount : 2,
              '--slidesToShow1022': itemCount < 4 ? itemCount : 3,
              '--containerWidthMobile': itemCount < 3 ? '100%' : '990px',
              '--containerWidth672':
                itemCount < 3 ? `calc((var(--articleCardWidth) + 20px) * var(--slidesToShow672))` : '660px',
              '--containerWidth1022':
                itemCount < 4 ? `calc((var(--articleCardWidth) + 20px) * var(--slidesToShow1022))` : '990px',
              '--containerWidth1292':
                itemCount < 4 ? `calc((var(--articleCardWidth) + 60px) * var(--slidesToShow1022))` : '1260px',
            } as React.CSSProperties
          }
        >
          {blogArticles.map((article) => (
            <div className={styles.cardWrapper} key={article.title}>
              <ArticleCard className={styles.articleCard} article={article} />
            </div>
          ))}
        </CarouselSlider>
        <CarouselPagination
          className={styles.carouselPagination}
          PageIndicatorComponent={({ index }) => (
            <CarouselPageIndicator className={styles.pageIndicator} activateOnIndex={index} />
          )}
        />
      </Carousel>
    </Section>
  );
};

export default ContentfulComponentRelatedArticles;

export const query = graphql`
  fragment ComponentRelatedArticles on ContentfulComponentRelatedArticles {
    __typename
    relatedArticlesTitle: title
    blogArticles {
      category
      slug
      title
      intro
      socialMediaImage {
        ...MixedMedia
      }
      featured
    }
    theme
  }
`;
